import { useState, useRef, useCallback, useEffect } from 'react';
import { viewNode } from '../../../app/settings';

/**
 * Hook that tracks scroll direction on the main view container
 * and returns a `hidden` state for scroll-aware header components.
 *
 * - Shows when scrolling up or at top/bottom edges
 * - Hides when scrolling down past the offset threshold
 * - Debounces state updates with a 60ms timeout
 */
export function useScrollHide(scrollOffset = 0) {
	const [hidden, setHidden] = useState(false);

	const lastScrollValueRef = useRef(0);
	const nextHiddenValueRef = useRef(false);
	const scrollOffsetRef = useRef(scrollOffset);
	const scrollTimeoutRef = useRef<number | undefined>(undefined);

	const onScrollStop = useCallback(() => {
		if (nextHiddenValueRef.current !== hidden) {
			setHidden(nextHiddenValueRef.current);
		}
	}, [hidden]);

	const onTouchEnd = useCallback(() => {
		if (nextHiddenValueRef.current !== hidden) {
			setHidden(nextHiddenValueRef.current);
		}
	}, [hidden]);

	const onScroll = useCallback(() => {
		const element = document.getElementById(viewNode)!;
		const scrollTop = element.scrollTop;
		const maxScroll = element.scrollHeight - element.clientHeight;
		const offset = scrollOffsetRef.current;

		if (scrollTop <= offset || scrollTop >= maxScroll - offset) {
			nextHiddenValueRef.current = false;
		} else if (scrollTop > lastScrollValueRef.current + offset) {
			nextHiddenValueRef.current = true;
			lastScrollValueRef.current = scrollTop;
		} else if (scrollTop < lastScrollValueRef.current - offset) {
			nextHiddenValueRef.current = false;
			lastScrollValueRef.current = scrollTop;
		}
		window.clearTimeout(scrollTimeoutRef.current);
		scrollTimeoutRef.current = window.setTimeout(onScrollStop, 60);
	}, [onScrollStop]);

	useEffect(() => {
		const element = document.getElementById(viewNode);
		if (element == null) {
			return;
		}
		element.addEventListener('scroll', onScroll);
		element.addEventListener('touchend', onTouchEnd);

		return () => {
			window.clearTimeout(scrollTimeoutRef.current);
			const el = document.getElementById(viewNode);
			if (el == null) {
				return;
			}
			el.removeEventListener('scroll', onScroll);
			el.removeEventListener('touchend', onTouchEnd);
		};
	}, [onScroll, onTouchEnd]);

	return hidden;
}
